import { useEffect, useState } from 'react'
import { View } from 'react-native'

import { useTheme } from '../theme/restyle'
import { isDarkColor } from '../theme/isDarkColor'
import {
  birthDateRange,
  parseCalendarDate,
  toCalendarDate,
} from './calendarDate'
import { KYC_FORM_METRICS } from './kycFormTheme'
import type { NativeDatePicker } from './nativeDatePicker'

/** iOS owns the wheel and its accessibility gestures (Figma 2017:20247). */
export function FiatBirthDateField({
  Picker,
  value,
  frameHeight,
  frameTop,
  editable,
  onChange,
}: {
  Picker: NativeDatePicker
  value: string
  /** The step's own frame, which the wheel is centred on. */
  frameHeight: number
  /** Everything drawn above the wheel, which its own middle is measured off. */
  frameTop: number
  editable: boolean
  onChange: (value: string) => void
}) {
  const theme = useTheme()
  const [range] = useState(() => birthDateRange())
  const [initialDate] = useState(
    () => new Date(range.maximumDate.getFullYear() - 18, 0, 1),
  )
  useEffect(() => {
    if (editable && value === '') {
      onChange(toCalendarDate(initialDate))
    }
  }, [editable, initialDate, onChange, value])

  return (
    <View
      style={{
        // Centred on the frame (2017:20247) rather than hung under the
        // heading. `frameTop` is everything standing above it, measured.
        marginTop: Math.max(
          24,
          frameHeight / 2 - frameTop - KYC_FORM_METRICS.dateWheelHeight / 2,
        ),
        height: KYC_FORM_METRICS.dateWheelHeight,
      }}
      pointerEvents={editable ? 'auto' : 'none'}
      accessibilityElementsHidden={!editable}
    >
      <Picker
        value={parseCalendarDate(value) ?? initialDate}
        mode="date"
        display="spinner"
        minimumDate={range.minimumDate}
        maximumDate={range.maximumDate}
        themeVariant={isDarkColor(theme.background.modal) ? 'dark' : 'light'}
        onChange={(event, date) => {
          if (editable && event.type !== 'dismissed' && date !== undefined) {
            onChange(toCalendarDate(date))
          }
        }}
      />
    </View>
  )
}
